import { useId, useState } from 'react'

/**
 * Disclosure list for the FAQ.
 *
 * Panels stay mounted so the CSS grid-row transition can animate them, which
 * means a collapsed panel is visually hidden but still in the DOM. `inert`
 * takes it out of the tab order and the accessibility tree - without it,
 * keyboard users would tab into invisible text.
 *
 * Several items may be open at once; the first starts open so the section does
 * not read as empty.
 *
 * @param {object} props
 * @param {Array<{q: string, a: string}>} props.items
 * @param {number|null} [props.initialOpen]
 */
export default function Accordion({ items, initialOpen = 0 }) {
  const baseId = useId()
  const [open, setOpen] = useState(() =>
    initialOpen === null ? new Set() : new Set([initialOpen]),
  )

  const toggle = (index) => {
    setOpen((current) => {
      const next = new Set(current)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  return (
    <div className="accordion">
      {items.map((item, index) => {
        const isOpen = open.has(index)
        const triggerId = `${baseId}-trigger-${index}`
        const panelId = `${baseId}-panel-${index}`

        return (
          <div className="accordion__item" key={item.q}>
            <h3>
              <button
                type="button"
                id={triggerId}
                className="accordion__trigger"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggle(index)}
              >
                {item.q}
                <span className="accordion__sign" aria-hidden="true" />
              </button>
            </h3>
            <div
              id={panelId}
              className="accordion__panel"
              data-open={isOpen}
              role="region"
              aria-labelledby={triggerId}
              inert={!isOpen}
            >
              <div>
                <p>{item.a}</p>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
